/**
 * Header Component - Sticky header with scroll behavior
 * Extends AKComponent and follows theme component architecture
 */

import { AKComponent } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { throttle, EventDebouncer } from '@theme/utilities';

export class HeaderComponent extends AKComponent {
    constructor() {
        super();
        
        this.lastScrollY = 0;
        this.eventDebouncer = new EventDebouncer();

        // Use throttled scroll handler from utilities
        this.handleScroll = throttle(this.handleScroll.bind(this), 16); // ~60fps
        this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
        super.connectedCallback();
        
        // Get the parent <header> element (generated by Shopify schema)
        this.headerElement = this.closest('header');
        if (!this.headerElement) {
            console.warn('HeaderComponent: Parent <header> element not found');
            return;
        }
        
        // Get sticky mode and home page options
        this.stickyMode = this.getAttribute('sticky');
        this.isTransparentSticky = this.hasAttribute('data-transparent-sticky');
        this.homeMode = this.getAttribute('data-home-mode');
        this.gradient = this.getAttribute('data-gradient');
        
        // Apply sticky class to parent header and header-component if sticky is enabled
        if (this.stickyMode && this.stickyMode !== 'disabled') {
            this.headerElement.classList.add('c-hdr--sticky');
            this.classList.add('c-hdr--sticky');
        }
        
        
        // Set gradient CSS variable if needed
        if (this.gradient) {
            this.style.setProperty('--header-gradient', this.gradient);
        }
        
        // Initialize scroll behavior (sticky + homepage)
        this.initScrollBehavior();
        
        // Always update header height
        this.updateHeaderHeight();
        window.addEventListener('resize', this.handleResize);
        
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        
        // Remove all event listeners
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);

        this.eventDebouncer.clearAll();

        // Reset header height
        document.body.style.setProperty('--header-height', '0px');
    }

    /**
     * Initialize scroll behavior (handles both sticky and homepage modes)
     */
    initScrollBehavior() {
        // Initial homepage state check if needed
        if (this.homeMode) {
            this.updateHomePageState();
        }
        
        // Add scroll listener if sticky is enabled OR homepage mode is active
        if ((this.stickyMode && this.stickyMode !== 'disabled') || this.homeMode) {
            window.addEventListener('scroll', this.handleScroll, { passive: true });
        }
    }

    /**
     * Handle scroll events (already throttled by utilities.throttle)
     */
    handleScroll() {
        // Update sticky state if enabled
        if (this.stickyMode && this.stickyMode !== 'disabled') {
            this.updateStickyState();
        }
        
        // Update home page state if enabled
        if (this.homeMode) {
            this.updateHomePageState();
        }
        
    }

    /**
     * Update sticky state based on scroll position
     * Applies classes to the parent <header> element
     */
    updateStickyState() {
        if (!this.headerElement) return;
        
        const currentScrollY = window.scrollY;
        const isScrollingDown = currentScrollY > this.lastScrollY;
        const scrollThreshold = 100;
        const scrollSpeed = Math.abs(currentScrollY - this.lastScrollY);
        const wasVisible = !this.headerElement.classList.contains('c-hdr--hide-on-scroll');

        // Add minimal scroll speed threshold to avoid micro-movements
        if (scrollSpeed < 2) return;

        if (this.stickyMode === 'always') {
            // Always visible sticky header
            this.headerElement.classList.remove('c-hdr--hide-on-scroll');
            this.headerElement.classList.remove('c-hdr--scroll-up', 'c-hdr--scroll-down');
            this.headerElement.classList.add(isScrollingDown ? 'c-hdr--scroll-down' : (currentScrollY > 0 ? 'c-hdr--scroll-up' : 'c-hdr--scroll-none'));
            
            // Apply transparent styling if enabled
            if (this.isTransparentSticky && currentScrollY > scrollThreshold) {
                this.headerElement.classList.add('c-hdr--transparent-sticky');
            } else {
                this.headerElement.classList.remove('c-hdr--transparent-sticky');
            }
            
        } else if (this.stickyMode === 'scroll-up') {
            // Show on scroll up only with smooth animations
            if (currentScrollY > scrollThreshold) {
                if (isScrollingDown) {
                    // Hide header smoothly when scrolling down
                    this.headerElement.classList.add('c-hdr--hide-on-scroll');
                    this.headerElement.classList.remove('c-hdr--show-on-scroll');
                    this.headerElement.classList.remove('c-hdr--scroll-up');
                    this.headerElement.classList.add('c-hdr--scroll-down');
                } else {
                    // Show header smoothly when scrolling up
                    this.headerElement.classList.remove('c-hdr--hide-on-scroll');
                    this.headerElement.classList.add('c-hdr--show-on-scroll');
                    this.headerElement.classList.remove('c-hdr--scroll-down');
                    this.headerElement.classList.add('c-hdr--scroll-up');

                    // Apply transparent styling if enabled
                    if (this.isTransparentSticky) {
                        this.headerElement.classList.add('c-hdr--transparent-sticky');
                    }

                }
            } else {
                // Always show header when near top with smooth transition
                this.headerElement.classList.remove('c-hdr--hide-on-scroll');
                this.headerElement.classList.add('c-hdr--show-on-scroll');
                this.headerElement.classList.remove('c-hdr--transparent-sticky');
                this.headerElement.classList.remove('c-hdr--scroll-up', 'c-hdr--scroll-down');
            }
        }

        // Dispatch visibility change event with debouncing to avoid spam
        const isVisible = !this.headerElement.classList.contains('c-hdr--hide-on-scroll');
        if (wasVisible !== isVisible) {
            
        }

        this.lastScrollY = currentScrollY;
    }

    /**
     * Update home page state (transparent/gradient when at top)
     * Works for both sticky and static headers
     * Applies classes to the parent <header> element and header-component
     */
    updateHomePageState() {
        if (!this.headerElement) return;
        
        const currentScrollY = window.scrollY;
        const atTopThreshold = 50; // Consider "at top" if within 50px of top
        const isAtTop = currentScrollY <= atTopThreshold;
        
        // Update classes for CSS on parent header element
        this.headerElement.classList.toggle('c-hdr--at-top', isAtTop);
        this.headerElement.classList.toggle('c-hdr--scrolled-down', !isAtTop);
        
        // Also add class to header-component for CSS selectors
        this.classList.toggle('c-hdr--scrolled-down', !isAtTop);
        
        if (this.homeMode === 'gradient') {
            // Apply gradient when at top
            this.headerElement.classList.toggle('c-hdr--gradient-active', isAtTop);
        }
        
        // No position transitions needed - CSS handles positioning based on sticky mode
    }


    /**
     * Handle window resize with debouncing
     */
    handleResize() {
        this.eventDebouncer.debounce('header-resize', () => {
            this.updateHeaderHeight();
        }, 250);
    }

    /**
     * Update the global header height CSS variable
     */
    updateHeaderHeight() {
        const height = this.offsetHeight;
        document.body.style.setProperty('--header-height', `${height}px`);
        
        // Also set the height on the header element for home-mode
        if (this.headerElement) {
        }
    }

}

// Register the custom element
if (!customElements.get('header-component')) {
    customElements.define('header-component', HeaderComponent);
}